"use client";

import { Show, UserButton } from "@clerk/nextjs";
import Link from "next/link";
import { Wordmark } from "./logo";
import { LinkButton } from "./ui";

export function Logo({ size = "md" }: { size?: "sm" | "md" }) {
  return (
    <Link
      href="/"
      className="flex shrink-0 items-center"
      aria-label="EasyShot — home"
      // 已在主页时拦截同页导航（它会打断平滑滚动），只滚回顶部
      onClick={(e) => {
        if (window.location.pathname === "/") {
          e.preventDefault();
          history.replaceState(null, "", "/");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }}
    >
      <Wordmark size={size === "sm" ? 17 : 19} />
    </Link>
  );
}

const NAV = [
  { href: "#features", label: "Features" },
  { href: "#how", label: "How it works" },
  { href: "#app", label: "Mobile app" },
  { href: "#pricing", label: "Pricing" },
];

export function SiteNav() {
  // 配了 Clerk 走真实登录页；demo 模式直接进 studio
  const clerkOn = Boolean(process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY);
  const signInHref = clerkOn ? "/sign-in" : "/studio/projects";
  return (
    <header className="sticky top-0 z-50 border-b border-line bg-[#08080c]/72 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-6 px-5">
        <Logo />
        <nav className="hidden items-center gap-1 md:flex">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className="rounded-full px-3.5 py-2 text-sm text-muted transition hover:bg-white/6 hover:text-foreground"
            >
              {n.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          {clerkOn ? (
            <>
              <Show when="signed-out">
                <LinkButton href={signInHref} variant="ghost" size="sm">
                  Sign in
                </LinkButton>
                <LinkButton href="/studio" size="sm">
                  Start free
                </LinkButton>
              </Show>
              <Show when="signed-in">
                <LinkButton href="/studio" variant="ghost" size="sm">
                  Studio
                </LinkButton>
                <UserButton />
              </Show>
            </>
          ) : (
            <>
              <LinkButton href={signInHref} variant="ghost" size="sm">
                Sign in
              </LinkButton>
              <LinkButton href="/studio" size="sm">
                Start free
              </LinkButton>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function SiteFooter() {
  const cols = [
    {
      title: "Product",
      links: [
        ["URL to Video", "/studio"],
        ["AI Avatars", "/studio"],
        ["Batch Generation", "/studio"],
        ["Projects", "/studio/projects"],
      ],
    },
    {
      title: "Solutions",
      links: [
        ["Cross-border sellers", "#"],
        ["Live-stream clips", "#"],
        ["Brand campaigns", "#"],
        ["Agencies", "#"],
      ],
    },
    {
      title: "Resources",
      links: [
        ["Pricing", "/#pricing"],
        ["Help center", "#"],
        ["Terms of Service", "/terms"],
        ["Privacy Policy", "/privacy"],
      ],
    },
  ];

  return (
    <footer className="mt-32 border-t border-line">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo />
          <p className="mt-4 max-w-xs text-[13px] leading-6 text-muted">
            The AI video factory for e-commerce teams. One product link in, a
            full set of ready-to-run ad creatives out.
          </p>
        </div>
        {cols.map((c) => (
          <div key={c.title}>
            <div className="mb-4 text-sm font-medium">{c.title}</div>
            <ul className="space-y-2.5">
              {c.links.map(([label, href]) => (
                <li key={label}>
                  <Link
                    href={href}
                    className="text-[13px] text-muted transition hover:text-foreground"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-line py-6 text-center text-xs text-muted">
        © {new Date().getFullYear()} EasyShot ·{" "}
        <Link href="/terms" className="hover:text-foreground">
          Terms
        </Link>{" "}
        ·{" "}
        <Link href="/privacy" className="hover:text-foreground">
          Privacy
        </Link>
      </div>
    </footer>
  );
}
